import React from 'react';
import { StyleSheet, css, CSSProperties } from 'aphrodite/no-important';
import { useTheme } from '../../../../theming/useTheme';
import { VrLineHeight } from '../../../../theming/verticalRhythm/verticalRhythm';
import { DropDownMenuLink } from './DropDownMenuLink';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';

interface DropDownMenuProps {
  configThemeKey: string
}

export interface DropDownMenuBaseThemeProps {
  menuBackgroundColor: string
  linkColor: string,
  hoverColor: string,
  activeColor: string,
  vrLineHeight: VrLineHeight
  surfaceStyle: CSSProperties
}

interface MenuLinkProps {
  text: string
  href: string
}

export interface DropDownMenuBaseProps {
  configThemeKey?: string
  menuLinks: MenuLinkProps[]
  highlightActive?: boolean
  activeEndpoint: string
}

type DropDownMenuCombinedProps = DropDownMenuBaseThemeProps & DropDownMenuBaseProps;

type ThemedDropDownMenu = DropDownMenuProps & DropDownMenuBaseProps;

const getStyle = (theme: DropDownMenuBaseThemeProps) => {
  const { vrLineHeight, menuBackgroundColor } = theme;

  return StyleSheet.create({
    menuWrapper: {
      position: 'absolute',
      top: '100%',
      width: '100%',
      minWidth: '160px',
      boxSizing: 'border-box',
      background: menuBackgroundColor,
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
      },
    },
  });
};

const getLinksList = (menuLinks: MenuLinkProps[], linkColor: string, hoverColor: string, activeColor: string, vrLineHeight: VrLineHeight, configThemeKey: string | undefined, activeEndpoint:string) => {
  const isActive = (link: MenuLinkProps) => link.href === activeEndpoint;
  return menuLinks.map((link, index) => (
    <DropDownMenuLink
      key={index}
      href={link.href}
      text={link.text}
      linkColor={linkColor}
      hoverColor={hoverColor}
      activeColor={activeColor}
      vrLineHeight={vrLineHeight}
      isActive={isActive(link)}
      configThemeKey={configThemeKey}
    />
  ));
};

export const DropDownMenuBase: React.FunctionComponent<DropDownMenuCombinedProps> = props => {
  const { surfaceStyle, vrLineHeight, menuLinks, configThemeKey, activeEndpoint } = props;
  const style = getStyle(props);
  const { linkColor, hoverColor, activeColor } = props;

  return (
    <ul className={css(style.menuWrapper, StyleSheet.create({ surfaceStyle }).surfaceStyle)}>
      {getLinksList(menuLinks, linkColor, hoverColor, activeColor, vrLineHeight, configThemeKey, activeEndpoint)}
    </ul>
  );
};

const mapThemeProps = (theme: DropDownMenuBaseThemeProps) => (
  {
    menuBackgroundColor: theme.menuBackgroundColor,
    linkColor: theme.linkColor,
    hoverColor: theme.hoverColor,
    activeColor: theme.activeColor,
    vrLineHeight: theme.vrLineHeight,
    surfaceStyle: theme.surfaceStyle,
  }
);

export const DropDownMenu: React.FunctionComponent<ThemedDropDownMenu> = (props: ThemedDropDownMenu) => {
  const { configThemeKey } = props;

  const DropDownMenuWithTheme = (menuProps: DropDownMenuBaseProps) => {
    const themeContext = useTheme(configThemeKey, mapThemeProps);
    return <ErrorBoundary><DropDownMenuBase {...themeContext} {...menuProps} configThemeKey={configThemeKey} /></ErrorBoundary>;
  };

  return <DropDownMenuWithTheme {...props} />;
};
